import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { ScrollArea } from './ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import {
  Mic, MicOff, Radio, Square, Loader2, Volume2,
  FileAudio, Clock, Sparkles, AlertCircle, CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Audio Capture Component for L10 Meeting Transcription
 * Uses MediaRecorder API to capture audio and send to backend for transcription
 */
const AudioCapture = ({
  meetingId,
  currentSection = 'segue',
  currentSpeaker = null,
  onTranscriptionReceived,
  enabled = false,
  onEnabledChange,
  className = ""
}) => {
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioLevel, setAudioLevel] = useState(0);
  
  // Transcription state
  const [transcriptions, setTranscriptions] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [chunkIndex, setChunkIndex] = useState(0);
  
  // Service status
  const [serviceAvailable, setServiceAvailable] = useState(null);
  
  // Refs
  const mediaRecorderRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const levelAnimationRef = useRef(null);
  
  // Configuration
  const CHUNK_DURATION_MS = 30000; // 30 seconds per chunk
  const MIME_TYPE = 'audio/webm;codecs=opus';
  
  // Check service availability on mount
  useEffect(() => {
    const checkService = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const response = await axios.get(`${API}/eos/transcription/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setServiceAvailable(response.data.available);
      } catch (error) {
        console.error('Error checking transcription service:', error);
        setServiceAvailable(false);
      }
    };
    
    checkService();
  }, []);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopRecording();
      if (levelAnimationRef.current) {
        cancelAnimationFrame(levelAnimationRef.current);
      }
    };
  }, []);
  
  // Timer for recording duration
  useEffect(() => {
    if (isRecording && !isPaused) {
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isRecording, isPaused]);
  
  // Analyze audio level
  const analyzeAudioLevel = useCallback(() => {
    if (!analyserRef.current) return;
    
    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteFrequencyData(dataArray);
    
    // Calculate average level
    const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
    setAudioLevel(Math.min(100, (average / 128) * 100));
    
    if (isRecording) {
      levelAnimationRef.current = requestAnimationFrame(analyzeAudioLevel);
    }
  }, [isRecording]);
  
  // Start recording
  const startRecording = async () => {
    try {
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } 
      });
      
      streamRef.current = stream;
      
      // Set up audio context for level monitoring
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);
      analyserRef.current.fftSize = 256;
      
      // Create MediaRecorder
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported(MIME_TYPE) ? MIME_TYPE : 'audio/webm'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        if (chunksRef.current.length > 0) {
          const blob = new Blob(chunksRef.current, { type: MIME_TYPE });
          await sendChunkForTranscription(blob);
          chunksRef.current = [];
        }
      };
      
      // Start recording with timeslice for chunking
      mediaRecorder.start(CHUNK_DURATION_MS);
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start level monitoring
      analyzeAudioLevel();
      
      toast.success('Recording started');
      
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('Failed to access microphone');
    }
  };
  
  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    
    if (levelAnimationRef.current) {
      cancelAnimationFrame(levelAnimationRef.current);
    }
    
    setIsRecording(false);
    setIsPaused(false);
    setAudioLevel(0);
    
    if (recordingTime > 0) {
      toast.info('Recording stopped');
    }
  };
  
  // Pause/resume recording
  const togglePause = () => {
    if (!mediaRecorderRef.current) return;
    
    if (isPaused) {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      analyzeAudioLevel();
      toast.info('Recording resumed');
    } else {
      mediaRecorderRef.current.pause();
      setIsPaused(false);
      if (levelAnimationRef.current) {
        cancelAnimationFrame(levelAnimationRef.current);
      }
      toast.info('Recording paused');
    }
  };
  
  // Send audio chunk for transcription
  const sendChunkForTranscription = async (blob) => {
    setIsProcessing(true);
    
    try {
      // Convert blob to base64
      const reader = new FileReader();
      const base64Promise = new Promise((resolve, reject) => {
        reader.onloadend = () => {
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
      });
      reader.readAsDataURL(blob);
      
      const audioBase64 = await base64Promise;
      
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API}/eos/meetings/${meetingId}/transcription/upload`,
        {
          audio_base64: audioBase64,
          file_format: 'webm',
          section: currentSection,
          speaker_hint: currentSpeaker,
          chunk_index: chunkIndex
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.success) {
        const newTranscription = {
          id: `chunk-${chunkIndex}`,
          text: response.data.text,
          section: currentSection,
          timestamp: new Date().toISOString(),
          duration: response.data.duration
        };
        
        setTranscriptions(prev => [...prev, newTranscription]);
        setChunkIndex(prev => prev + 1);
        
        // Notify parent
        onTranscriptionReceived?.(newTranscription);
        
        toast.success('Audio transcribed successfully');
      } else {
        toast.error(response.data.error || 'Transcription failed');
      }
      
    } catch (error) {
      console.error('Error sending audio for transcription:', error);
      toast.error('Failed to transcribe audio');
    } finally {
      setIsProcessing(false);
    }
  };
  
  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Render service unavailable state
  if (serviceAvailable === false) {
    return (
      <Card className={`bg-slate-900 border-slate-800 ${className}`}>
        <CardContent className="py-6 text-center">
          <AlertCircle className="h-10 w-10 mx-auto mb-3 text-yellow-500" />
          <h3 className="font-semibold text-white mb-1">Transcription Unavailable</h3>
          <p className="text-sm text-slate-400">
            The transcription service is not configured. Contact your administrator.
          </p>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card className={`bg-slate-900 border-slate-800 ${className}`}>
      <CardHeader className="py-3 px-4 border-b border-slate-800">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <FileAudio className="h-4 w-4 text-purple-400" />
            Live Transcription
            {serviceAvailable && (
              <Badge variant="outline" className="text-[10px] border-green-500 text-green-400">
                <CheckCircle2 className="h-2.5 w-2.5 mr-1" />
                Ready
              </Badge>
            )}
          </CardTitle>
          
          <div className="flex items-center gap-2">
            <Label htmlFor="transcription-enabled" className="text-xs text-slate-400">
              Enable
            </Label>
            <Switch
              id="transcription-enabled"
              checked={enabled}
              onCheckedChange={onEnabledChange}
              disabled={isRecording}
            />
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="p-4 space-y-4">
        {/* Recording Status */}
        {enabled && (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Recording indicator */}
              <div className={`relative ${isRecording ? 'animate-pulse' : ''}`}>
                {isRecording ? (
                  <Radio className="h-5 w-5 text-red-500" />
                ) : (
                  <Mic className="h-5 w-5 text-slate-400" />
                )}
                {isRecording && (
                  <span className="absolute -top-1 -right-1 h-2 w-2 bg-red-500 rounded-full" />
                )}
              </div>
              
              {/* Time */}
              <div className="flex items-center gap-1 text-sm">
                <Clock className="h-3.5 w-3.5 text-slate-500" />
                <span className={isRecording ? 'text-white' : 'text-slate-500'}>
                  {formatTime(recordingTime)}
                </span>
              </div>
              
              {/* Audio level */}
              {isRecording && (
                <div className="flex items-center gap-1">
                  <Volume2 className="h-3.5 w-3.5 text-slate-500" />
                  <div className="w-16 h-2 bg-slate-700 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-green-500 transition-all duration-100"
                      style={{ width: `${audioLevel}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
            
            {/* Controls */}
            <div className="flex items-center gap-2">
              {!isRecording ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        className="bg-red-600 hover:bg-red-700"
                        onClick={startRecording}
                        disabled={isProcessing}
                      >
                        <Mic className="h-4 w-4 mr-1" />
                        Start
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Start recording meeting audio</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={togglePause}
                          disabled={isProcessing}
                        >
                          {isPaused ? 'Resume' : 'Pause'}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{isPaused ? 'Resume recording' : 'Pause recording'}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={stopRecording}
                          disabled={isProcessing}
                        >
                          <Square className="h-4 w-4 mr-1" />
                          Stop
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Stop recording</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </>
              )}
            </div>
          </div>
        )}
        
        {/* Processing indicator */}
        {isProcessing && (
          <div className="flex items-center gap-2 text-purple-400 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" />
            Transcribing audio...
          </div>
        )}
        
        {/* Recent transcriptions */}
        {transcriptions.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <Sparkles className="h-3.5 w-3.5" />
              Recent Transcriptions ({transcriptions.length})
            </div>
            
            <ScrollArea className="h-32 rounded-md border border-slate-800 bg-slate-950">
              <div className="p-2 space-y-2">
                {transcriptions.slice(-5).reverse().map((t, idx) => (
                  <div key={t.id} className="text-xs">
                    <div className="flex items-center gap-1 text-slate-500 mb-0.5">
                      <Badge variant="outline" className="text-[9px] px-1 py-0">
                        {t.section}
                      </Badge>
                      <span>{new Date(t.timestamp).toLocaleTimeString()}</span>
                    </div>
                    <p className="text-slate-300 line-clamp-2">{t.text}</p>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}
        
        {/* Instructions when disabled */}
        {!enabled && (
          <div className="text-center py-4">
            <MicOff className="h-8 w-8 mx-auto mb-2 text-slate-600" />
            <p className="text-sm text-slate-500">
              Enable transcription to capture meeting audio
            </p>
            <p className="text-xs text-slate-600 mt-1">
              Audio will be transcribed using AI in real-time
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AudioCapture;
